import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { APP_NAME, homeFor } from "@/config";
import { useConfig } from "@/lib/flags";
import { answerWithPasskey, passkeysSupported, passkeyError } from "@/lib/webauthn";
import { LegalLinks } from "@/components/Legal";
import { ProviderLogo } from "@/components/ProviderLogo";
import { PROVIDER_NAMES, startSocial } from "@/lib/social";
import { GraphiteScope, Wordmark } from "@/components/Graphite";
import { takeReturn } from "@/lib/returnTo";

// Sign in, sign up, invites and password resets. Graphite: the left half is a dark hero in both themes,
// with a two-tone headline; the form sits on the page ground.
export function AuthShell({ title, subtitle, children }) {
  return (
    <GraphiteScope className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="dark gr-hero hidden lg:flex flex-col justify-between p-12 border-r text-foreground">
        <Wordmark />
        <div className="max-w-lg">
          <h2 className="font-display text-h2 text-balance">
            <span className="block font-normal text-muted-foreground">Private, budgeted AI coding sessions</span>
            <span className="block font-semibold">for your team.</span>
          </h2>
          <p className="text-lead font-light text-gr-ink-secondary mt-5">
            Give your team and independent professionals isolated AI sessions on your own workspace server. Set objectives and
            budgets, track every commit and dollar — never their prompts. Content stays encrypted and theirs alone.
          </p>
          <div className="flex gap-2 mt-8 text-sm">
            <span className="px-3 py-1 rounded-md bg-gr-surface-2">Claude Agent (Anthropic)</span>
            <span className="px-3 py-1 rounded-md bg-gr-surface-2">Codex (OpenAI)</span>
          </div>
        </div>
        <div className="text-xs uppercase tracking-[0.09em] text-muted-foreground">AES-256-GCM · per-session isolation · audit hash chain</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-display font-semibold text-[20px] leading-[1.2] tracking-[0.02em] sm:text-h3 text-balance">{title}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{subtitle}</p>
          {children}
          <LegalLinks className="mt-10" />
        </div>
      </div>
    </GraphiteScope>
  );
}

export { homeFor };

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);  // false, "password" or "passkey": which button spins
  const { setSession } = useAuth();
  const navigate = useNavigate();
  // demo deployments only (SEED_DEMO); production serves none
  const config = useConfig();
  const demo = config.demo_accounts || [];
  const flags = config.flags || {};

  const home = homeFor;
  const social = config.social || [];
  const socialLogin = async (provider) => {
    setLoading(provider);
    try { await startSocial(provider); } catch (err) { toast.error(apiErr(err)); setLoading(false); }
  };

  // signing in with a passkey needs no email: the browser offers the passkeys it has for this site
  const passkeyLogin = async () => {
    setLoading("passkey");
    try {
      const { data: got } = await api.post("/auth/passkey/options");
      const credential = await answerWithPasskey(got.options);
      const { data } = await api.post("/auth/passkey", { challenge_id: got.challenge_id, credential });
      setSession(data);
      navigate(takeReturn(home(data.user.role)));
      toast.success(`Welcome, ${data.user.name}`);
    } catch (err) {
      toast.error(passkeyError(err) || apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const doLogin = async (e, creds) => {
    e?.preventDefault?.();
    const body = creds || { email, password };
    setLoading("password");
    try {
      const { data } = await api.post("/auth/login", body);
      setSession(data);
      const r = data.user.role;
      navigate(takeReturn(home(r)));
      toast.success(`Welcome, ${data.user.name}`);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle={`Access your ${APP_NAME} workspace`}>
      <form onSubmit={doLogin} className="space-y-4">
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.dev" autoComplete="username" />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          <Input data-testid="login-password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
        </div>
        <Button type="submit" className="w-full" disabled={!!loading} data-testid="login-submit">
          {loading === "password" && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
        </Button>
        {flags.webauthn && passkeysSupported() && (
          <Button type="button" variant="outline" className="w-full" disabled={!!loading} onClick={passkeyLogin}
            data-testid="passkey-login">
            {loading === "passkey" ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Sign in with a passkey
          </Button>
        )}
        {social.map((provider) => (
          <Button key={provider} type="button" variant="outline" className="w-full" disabled={!!loading}
            onClick={() => socialLogin(provider)} data-testid={`social-login-${provider}`}>
            {loading === provider ? <Loader2 className="w-4 h-4 animate-spin" /> : <ProviderLogo provider={provider} />}
            Continue with {PROVIDER_NAMES[provider]}
          </Button>
        ))}
        {flags.email && (
          <Link to="/forgot-password" className="block text-center text-sm text-muted-foreground hover:text-foreground"
            data-testid="forgot-link">Forgot password?</Link>
        )}
      </form>
      {demo.length > 0 && (
        <div className="mt-6">
          <div className="text-sm uppercase tracking-[0.09em] text-muted-foreground mb-2">Demo accounts</div>
          <div className="grid gap-2">
            {demo.map(({ label, email: em, password: pw }) => (
              <Button key={em} variant="outline" size="sm" className="justify-between px-4"
                data-testid={`demo-login-${label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => doLogin(null, { email: em, password: pw })}>
                <span>{label}</span><span className="font-sans font-normal text-muted-foreground">{em}</span>
              </Button>
            ))}
          </div>
        </div>
      )}
      <p className="text-sm text-muted-foreground mt-6 text-center">
        New company? <Link to="/signup" className="text-foreground underline" data-testid="go-signup">Create account</Link>
      </p>
    </AuthShell>
  );
}
